import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Users, CalendarCheck, MapPin, Building } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

const StatCard = ({ label, value, icon: Icon, accent, testid }) => (
  <Card className="p-5 sm:p-6 border-slate-200" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
          {label}
        </p>
        <p className="font-heading text-3xl font-extrabold text-slate-900 mt-2">{value}</p>
      </div>
      <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [munis, setMunis] = useState([]);
  const [districtId, setDistrictId] = useState("all");
  const [muniId, setMuniId] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/registrations/stats").then((r) => setStats(r.data)),
      api.get("/districts").then((r) => setDistricts(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (districtId === "all") {
      setMunis([]);
      setMuniId("all");
    } else {
      api.get(`/districts/${encodeURIComponent(districtId)}/municipalities`).then((r) => setMunis(r.data));
      setMuniId("all");
    }
  }, [districtId]);

  useEffect(() => {
    const params = {};
    if (districtId !== "all") params.district = districtId;
    if (muniId !== "all") params.municipality = muniId;
    api.get("/registrations/aggregate", { params }).then((r) => setRows(r.data));
  }, [districtId, muniId]);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
          Haryana Safai Mahakumbh
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Overview of citizen registrations across all urban local bodies.
        </p>
      </div>

      {loading ? (
        <PageLoader label="Loading dashboard…" />
      ) : (
      <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          testid="stat-total-registrations"
          label="Total Registrations"
          value={stats?.total ?? "—"}
          icon={Users}
          accent="bg-blue-900"
        />
        <StatCard
          testid="stat-today-registrations"
          label="Today"
          value={stats?.today ?? "—"}
          icon={CalendarCheck}
          accent="bg-teal-600"
        />
        <StatCard
          testid="stat-districts-active"
          label="Districts Active"
          value={stats?.districts_active ?? "—"}
          icon={MapPin}
          accent="bg-emerald-600"
        />
        <StatCard
          testid="stat-municipalities-covered"
          label="Municipalities"
          value={stats?.municipalities_covered ?? "—"}
          icon={Building}
          accent="bg-amber-600"
        />
      </div>

      {stats?.by_district?.length > 0 && (
        <Card className="p-5 sm:p-6 border-slate-200">
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
            Registrations by District
          </h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.by_district}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="district" tick={{ fontSize: 12, fill: "#475569" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#475569" }} />
                <Tooltip cursor={{ fill: "#eff6ff" }} />
                <Bar dataKey="count" fill="#1e3a8a" radius={[6, 6, 0, 0]} maxBarSize={64} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <Card className="p-5 sm:p-6 border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h2 className="font-heading text-lg font-semibold text-slate-900">
            District & Municipality Breakdown
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={districtId} onValueChange={setDistrictId}>
              <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="filter-district-select">
                <SelectValue placeholder="All Districts" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Districts</SelectItem>
                {districts.map((d) => (
                  <SelectItem key={d.id} value={String(d.id)}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={muniId}
              onValueChange={setMuniId}
              disabled={districtId === "all"}
            >
              <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="filter-municipality-select">
                <SelectValue placeholder="All Municipalities" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Municipalities</SelectItem>
                {munis.map((m) => (
                  <SelectItem key={m.id} value={m.name}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <Table data-testid="aggregate-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>District</TableHead>
                <TableHead>Municipality</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Registrations</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No registrations found for this filter.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={`${r.district}-${r.municipality}`}>
                    <TableCell className="font-medium">{r.district}</TableCell>
                    <TableCell>{r.municipality}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-normal">
                        {r.municipality_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-blue-900">
                      {r.count}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
      </>
      )}
    </div>
  );
}
